import {
  createPath,
  generatePath,
  type LoaderFunction,
  type Params,
  type PathParam,
  type RouteObject,
  type ShouldRevalidateFunction,
  type UIMatch,
  useMatches,
} from '@github-ui/react-router'
import {IS_BROWSER, IS_SERVER, ssrSafeLocation} from '@github-ui/ssr-utils'
import {sendCustomMetric} from '@github-ui/stats'
import {queryOptions, type SkipToken, type UseQueryOptions} from '@tanstack/react-query'

import type {EmbeddedData} from '../embedded-data-types'
import {getQueryClient} from '../query-client'
import {makeQueryKey as buildQueryKey, queryKeyObjFromKey, type RouteQueryKey} from '../query-key'
import {
  type ComponentRenderingProperties,
  type NonNullableType,
  type QueryDepsFn,
  type QueryRouteQueryConfig,
  QueryRouteQueryType,
} from './data-router-types'
import {wrapComponentWithProfiler} from './WrapWithProfiler'

type QueryOptions = Omit<Parameters<typeof queryOptions>[0], 'queryFn' | 'queryKey' | 'enabled'>

type GetEmbeddedDataFn = () => EmbeddedData | undefined

type QueryDepsFromConfig<Config> =
  Config extends QueryRouteQueryConfig<
    string,
    string,
    infer RoutePath extends string,
    string,
    infer Deps,
    unknown,
    QueryRouteQueryType
  >
    ? Deps extends QueryDepsFn<RoutePath>
      ? ReturnType<Deps>
      : Record<string, unknown>
    : Record<string, unknown>

type QueriesWithHelpers<
  AppName extends string,
  RouteId extends string,
  RoutePath extends string,
  Queries extends Record<
    string,
    QueryRouteQueryConfig<
      AppName,
      RouteId,
      RoutePath,
      string,
      QueryDepsFn<RoutePath> | undefined,
      unknown,
      QueryRouteQueryType
    >
  >,
> = {
  [Key in keyof Queries]: Queries[Key] & {
    makeQueryKey: (queryDeps?: QueryDepsFromConfig<Queries[Key]>) => RouteQueryKey
  }
}

export type QueryOptionsWithKey = UseQueryOptions<unknown, Error, unknown> & {
  initialData?: unknown
} & {
  queryKey: RouteQueryKey
  queryFn: Exclude<UseQueryOptions<unknown, Error, unknown>['queryFn'], SkipToken>
}

type RouteMatchData = {
  route: QueryRoute<
    string,
    string,
    string,
    Record<
      string,
      QueryRouteQueryConfig<string, string, string, string, QueryDepsFn<string>, string, QueryRouteQueryType>
    >
  >
  queries: Record<string, {type: QueryRouteQueryType; queryConfig: QueryOptionsWithKey}>
}

export type RouteMatches = Array<UIMatch<RouteMatchData | null>>

interface QueryMeta {
  preloadedAt?: number
}

const PreloadStatus = {
  Fresh: 'fresh',
  Stale: 'stale',
  None: 'none',
  Error: 'error',
} as const
type PreloadStatus = (typeof PreloadStatus)[keyof typeof PreloadStatus]

/**
 * Checks if a query was preloaded within the preload stale time window
 * by examining the query's meta.preloadedAt timestamp.
 */
function getPreloadStatus(queryClient: ReturnType<typeof getQueryClient>, queryKey: RouteQueryKey): PreloadStatus {
  const query = queryClient.getQueryCache().find({queryKey})
  const meta = query?.meta as QueryMeta | undefined

  if (!query || !meta?.preloadedAt) return PreloadStatus.None
  if (query.state.status === 'error') return PreloadStatus.Error

  const preloadAge = Date.now() - meta.preloadedAt

  if (preloadAge > DEFAULT_STALE_TIME_FOR_PRELOAD) return PreloadStatus.Stale

  return PreloadStatus.Fresh
}

export function useRouteMatches() {
  return useMatches() as RouteMatches
}

const QUERY_ROUTE_QUERY_CLIENT_DEFAULTS: QueryOptions = {
  refetchOnWindowFocus: false,
  retry: false,
  networkMode: 'always',
  staleTime: 1000 * 60 * 60 * 24, // 1 day in ms
}

export class MissingSSREmbeddedDataError extends Error {
  readonly queryName: string
  readonly routeId: string
  readonly routePath: string

  constructor({queryName, routeId, routePath}: {queryName: string; routeId: string; routePath: string}) {
    super(
      `[SSR] Blocking query "${queryName}" on route "${routeId}" is missing embedded data. ` +
        `Blocking queries require embedded data during server rendering. ` +
        `Ensure the server payload includes data for this query. ` +
        `Route path: "${routePath}"`,
    )
    this.name = 'MissingSSREmbeddedDataError'
    this.queryName = queryName
    this.routeId = routeId
    this.routePath = routePath
  }
}

export const DEFAULT_STALE_TIME_FOR_NAVIGATION = 200
export const DEFAULT_STALE_TIME_FOR_PRELOAD = 1000 * 30 // 30 seconds

export class QueryRoute<
  AppName extends string,
  RouteId extends string,
  RoutePath extends string,
  Queries extends Record<
    string,
    QueryRouteQueryConfig<
      AppName,
      RouteId,
      RoutePath,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any, // QueryName
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any, // QueryDeps
      unknown, // QueryRes
      QueryRouteQueryType
    >
  >,
> {
  #appName: AppName
  #getEmbeddedData: GetEmbeddedDataFn
  #shouldRevalidate?: ShouldRevalidateFunction

  id: RouteId
  path: RoutePath
  queries: QueriesWithHelpers<AppName, RouteId, RoutePath, Queries>
  index: boolean

  constructor(args: {
    appName: AppName
    id: RouteId
    path: RoutePath
    queries: Queries
    index: boolean
    getEmbeddedData: GetEmbeddedDataFn
    shouldRevalidate?: ShouldRevalidateFunction
  }) {
    this.#appName = args.appName
    this.id = args.id
    this.path = args.path
    this.queries = this.#processQueries(args.queries)
    this.index = args.index
    this.#getEmbeddedData = args.getEmbeddedData
    this.#shouldRevalidate = args.shouldRevalidate
  }

  #processQueries(queries: Queries): QueriesWithHelpers<AppName, RouteId, RoutePath, Queries> {
    return Object.fromEntries(
      objectEntries(queries).map(([queryName, queryConfig]) => {
        const makeQueryKey = (queryDeps?: QueryDepsFromConfig<typeof queryConfig>) =>
          buildQueryKey({
            appName: this.#appName,
            routeId: this.id,
            routePath: this.path,
            queryName: queryName.toString(),
            queryDeps: (queryDeps ?? {}) as Record<string, unknown>,
          })

        return [
          queryName,
          {
            ...queryConfig,
            makeQueryKey,
          },
        ]
      }),
    ) as QueriesWithHelpers<AppName, RouteId, RoutePath, Queries>
  }

  /**
   * Validates whether an argument is the same as the route
   *
   * We don't want to do a pure equality check like `Object.is` since on hydration
   * we might have different/similar objects.
   *
   * Doing a simple `id === id` check since this is most commonly going to catch issues
   * should there be any without doing a real deepEquality check
   */
  isSameRoute(route: unknown): route is QueryRoute<AppName, RouteId, RoutePath, Queries> {
    return Boolean(
      typeof route === 'object' && route && 'id' in route && typeof route.id === 'string' && route.id === this.id,
    )
  }

  /**
   * Given a params object generates a valid pathname for the route.
   * Optionally search parameters and/or a hash can be passed.
   */
  generatePath(
    params: {[key in PathParam<RoutePath>]: string},
    args?: {
      search?: ConstructorParameters<typeof URLSearchParams>[0]
      hash?: string
    },
  ) {
    return createPath({
      pathname: generatePath(this.path, params),
      search: args?.search ? new URLSearchParams(args.search).toString() : undefined,
      hash: args?.hash,
    })
  }

  #initializeQueryFromEmbeddedData({
    queryName,
    queryKey,
    type,
  }: {
    queryName: keyof Queries
    queryKey: ReturnType<typeof queryOptions>['queryKey']
    type: QueryRouteQueryType
  }) {
    const queryClient = getQueryClient()
    const embeddedData = this.#getEmbeddedData()
    const embeddedDataPayload = embeddedData?.payload as Record<string, Record<keyof Queries, unknown>> | undefined
    const initialData = this.#buildInitialDataForQueryClient({embeddedData, queryName})

    // Note that embedded data is not keyed by `queryDeps`, so it could be applied to any query with the same
    // `routeId` and `queryName`.
    // Because of this, we only want it to apply on initial render, so we delete it after first access.
    if (initialData !== undefined) {
      if (queryName === 'mainQuery') {
        delete embeddedDataPayload?.[this.id]
      } else {
        delete embeddedDataPayload?.[String(queryName)]
        delete embeddedDataPayload?.[this.id]?.[queryName]
      }

      queryClient.setQueryData<typeof initialData>(queryKey, initialData)
    } else if (IS_SERVER && type === QueryRouteQueryType.Blocking) {
      // During SSR, blocking queries MUST have their data provided via embedded data.
      // Fail hard so we don't silently render with missing data.
      throw new MissingSSREmbeddedDataError({
        queryName: String(queryName),
        routeId: this.id,
        routePath: this.path,
      })
    }
  }

  /**
   * The react-route compatible loader. This loops through each query defined on the route,
   * fetching or preloading the data as necessary.
   */
  #loader: LoaderFunction = async ({request, params}) => {
    const blockingRequests: Array<Promise<unknown>> = []
    const {searchParams} = new URL(request.url, ssrSafeLocation.origin)

    const pathname = toQualifiedPath(this.path, params)
    const queryClient = getQueryClient()
    let preloadedQueries = 0
    let totalEligibleQueries = 0

    const queryConfigs = objectEntries(this.queries).map(
      ([
        queryName,
        {
          queryFn,
          queryDeps,
          makeQueryKey,
          type = QueryRouteQueryType.Deferred,
          staleTimeForNavigation = DEFAULT_STALE_TIME_FOR_NAVIGATION,
          navigationBehavior = 'stale-while-revalidate',
          enabled,
          ...config
        },
      ]) => {
        const deps =
          queryDeps?.({
            pathname,
            params: params as NonNullableType<Params<PathParam<RoutePath>>>,
            searchParams,
          }) ?? {}

        const queryKey = makeQueryKey(deps)

        const queryConfig = queryOptions({
          ...QUERY_ROUTE_QUERY_CLIENT_DEFAULTS,
          queryKey,
          queryFn: ({signal, meta}) => {
            return queryFn(queryKeyObjFromKey(queryKey), {signal, meta})
          },
          enabled,
          ...(config as QueryOptions),
        })

        const isEnabled = typeof enabled === 'function' ? enabled() : (enabled ?? true)

        if (isEnabled) {
          // Only initialize from embedded data if the query doesn't already have cached data
          // (e.g., from a preload). This prevents overwriting preloaded data and resetting dataUpdatedAt.
          const existingData = queryClient.getQueryData(queryConfig.queryKey)
          if (existingData === undefined) {
            this.#initializeQueryFromEmbeddedData({queryName, queryKey: queryConfig.queryKey, type})
          }
        }

        // Don't try to begin queries on the server, or when explicitly disabled
        if (IS_BROWSER && isEnabled) {
          totalEligibleQueries++

          const fetchConfig = {
            ...queryConfig,
            // during navigation we set the staletime very short to ensure fresh data is pulled, but not too aggressively as to get caught during hydration for a server hydrated query
            staleTime: staleTimeForNavigation,
            meta: {...queryConfig.meta} satisfies QueryMeta,
          }

          // Check if this specific query was recently preloaded (via hover/focus preloading)
          // If so, we can skip refetching to avoid unnecessary network requests
          const preloadStatus = getPreloadStatus(queryClient, queryKey)
          const freshPreload = preloadStatus === PreloadStatus.Fresh

          if (freshPreload) {
            preloadedQueries++
          } else if (preloadStatus !== PreloadStatus.None) {
            sendCustomMetric({
              name: 'REACT_DATA_ROUTER_PRELOAD_REJECTED',
              value: 1,
              tags: {
                routeId: this.id,
                reason: preloadStatus,
                appName: this.#appName,
                router: 'data-router',
              },
            })
          }

          switch (type) {
            case QueryRouteQueryType.Deferred: {
              /**
               * Deferred queries use prefetchQuery to avoid throwing/rejecting on failure.
               * We initiate these requests during navigation, but we don't await them, so they
               * can resolve anytime between now and well after navigation completes.
               *
               * Skip preloading if data was recently preloaded to avoid unnecessary network requests.
               */
              if (!freshPreload) {
                void queryClient.prefetchQuery(fetchConfig)
              }
              break
            }
            case QueryRouteQueryType.Blocking: {
              /**
               * Immediately returns stale data if it exists, but fetches updates in the background
               * this gives us a default `stale-while-revalidate` behavior.
               *
               * We may later make this configureable with a `blockingQueryBehavior` of `stale-while-revalidate` or `revalidate`
               */
              const state = queryClient.getQueryState(fetchConfig.queryKey)
              const dataAge = state ? Date.now() - state.dataUpdatedAt : Infinity

              // Check if query data doesn't exist, is currently pending, or previously errored.
              // Use fetchStatus to detect in-flight requests to avoid race conditions
              // where rapid double-clicks might not see the query as pending yet.
              // Queries in error state must be re-fetched and awaited to avoid rendering
              // the component with undefined data.
              if (
                !state ||
                state.status === 'pending' ||
                state.status === 'error' ||
                state.fetchStatus === 'fetching'
              ) {
                // fetchQuery will deduplicate in-flight requests automatically
                blockingRequests.push(queryClient.fetchQuery(fetchConfig))
              } else if (navigationBehavior === 'network-first') {
                // Network-first always fetches fresh data, regardless of preload status
                if (dataAge >= staleTimeForNavigation) {
                  void queryClient.invalidateQueries({queryKey: fetchConfig.queryKey})
                  blockingRequests.push(queryClient.fetchQuery(fetchConfig))
                }
              } else if (!freshPreload && dataAge >= staleTimeForNavigation) {
                // For stale-while-revalidate: only invalidate if data wasn't recently preloaded
                // This prevents unnecessary background refetches when navigating after a preload
                void queryClient.invalidateQueries({queryKey: fetchConfig.queryKey})
              }
              break
            }
            default: {
              throw new Error(
                `Invalid QueryRouteQueryType defined, \`${type}\`. Valid QueryRouteQueryTypes are ${JSON.stringify(
                  Object.keys(QueryRouteQueryType),
                )}`,
              )
            }
          }
        }

        return [queryName, {queryConfig, type}] as const
      },
    )

    await Promise.all(blockingRequests)

    if (IS_BROWSER && preloadedQueries > 0) {
      sendCustomMetric({
        name: 'REACT_DATA_ROUTER_PRELOAD_HIT',
        value: 1,
        tags: {
          routeId: this.id,
          type: preloadedQueries === totalEligibleQueries ? 'full' : 'partial',
          appName: this.#appName,
          router: 'data-router',
        },
      })
    }

    return {
      route: this,
      queries: Object.fromEntries(queryConfigs),
    }
  }

  /**
   * Given components and children to render, returns a react-router compatible route
   * definition implementing the `QueryRoute` logic.
   */
  toRoute = ({Component, element, ...args}: Pick<RouteObject, ComponentRenderingProperties>): RouteObject => {
    const WrappedComponent = wrapComponentWithProfiler(this.id, {element, Component})
    // Default to not revalidating - routes should opt-in to revalidation
    const shouldRevalidate = this.#shouldRevalidate
    const handle = {
      ...args.handle,
      queryRoute: this,
    }
    if (this.index) {
      return {
        ...args,
        id: this.id,
        children: undefined,
        path: this.path,
        index: this.index,
        loader: this.#loader,
        handle,
        shouldRevalidate,
        Component: WrappedComponent,
      }
    }

    return {
      ...args,
      id: this.id,
      path: this.path,
      index: this.index,
      loader: this.#loader,
      handle,
      shouldRevalidate,
      Component: WrappedComponent,
    }
  }

  #buildInitialDataForQueryClient({
    embeddedData,
    queryName,
  }: {
    embeddedData: EmbeddedData | undefined
    queryName: keyof Queries
  }) {
    // embedded data may be keyed directly by `embeddedData.payload[queryName]`
    // or nested under a route id as `embeddedData.payload[routeId][queryName]`
    const embeddedDataPayload = embeddedData?.payload as Record<string, Record<keyof Queries, unknown>> | undefined
    let routePayload
    if (queryName === 'mainQuery') {
      routePayload = embeddedDataPayload?.[this.id]
    } else {
      routePayload = embeddedDataPayload?.[String(queryName)] ?? embeddedDataPayload?.[this.id]?.[queryName]
    }

    if (routePayload === undefined) {
      return
    }

    if (queryName !== 'mainQuery') {
      return routePayload
    }

    const embeddedDataTitle = embeddedData?.title || embeddedData?.meta?.title

    return {
      meta: embeddedDataTitle ? {title: embeddedDataTitle} : undefined,
      payload: routePayload,
    }
  }

  /**
   * Preloads all queries for this route without blocking.
   *
   * @param params - Route params matching the route's path pattern
   * @param searchParams - Optional URL search parameters
   */
  preload(params: {[key in PathParam<RoutePath>]: string}, searchParams?: URLSearchParams): void {
    if (!IS_BROWSER) return

    const pathname = toQualifiedPath(this.path, params)
    const queryClient = getQueryClient()
    const resolvedSearchParams = searchParams ?? new URLSearchParams()

    for (const [
      _queryName,
      {queryFn, queryDeps, makeQueryKey, navigationBehavior, enabled, ...config},
    ] of objectEntries(this.queries)) {
      // Skip preloading for network-first queries since they will always fetch fresh data on navigation
      if (navigationBehavior === 'network-first') {
        continue
      }

      // Skip preloading for disabled queries
      const isEnabled = typeof enabled === 'function' ? enabled() : (enabled ?? true)
      if (!isEnabled) {
        continue
      }

      const deps =
        queryDeps?.({
          pathname,
          params: params as NonNullableType<Params<PathParam<RoutePath>>>,
          searchParams: resolvedSearchParams,
        }) ?? {}

      const queryKey = makeQueryKey(deps)

      // Skip if this query was already recently preloaded
      if (getPreloadStatus(queryClient, queryKey) === PreloadStatus.Fresh) {
        continue
      }

      void queryClient.prefetchQuery({
        ...QUERY_ROUTE_QUERY_CLIENT_DEFAULTS,
        queryKey,
        queryFn: ({signal, meta}) => {
          return queryFn(queryKeyObjFromKey(queryKey), {signal, meta})
        },
        ...(config as QueryOptions),
        meta: {...(config as QueryOptions).meta, preloadedAt: Date.now()} satisfies QueryMeta,
      })
    }

    sendCustomMetric({
      name: 'REACT_DATA_ROUTER_PRELOAD_COUNT',
      value: 1,
      tags: {
        routeId: this.id,
        appName: this.#appName,
        router: 'data-router',
      },
    })
  }
}

/**
 * A QueryRoute with any queries — used when the specific query shape doesn't matter.
 * */
export type AnyQueryRoute = QueryRoute<
  string,
  string,
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<string, QueryRouteQueryConfig<any, any, any, any, any, any, any>>
>

/**
 * Given a `routePath` path pattern and a loader-like params object return a
 * string with the params replacing the pattern
 */
function toQualifiedPath<RoutePath extends string>(routePath: RoutePath, params: Params<PathParam<RoutePath>>) {
  return generatePath(routePath, translateUndefinedToNull(params))
}

/**
 * Given an object with potentially undefined parameters map those to `null`
 *
 * This is mostly useful for mapping an object from a loader params shape
 * to a generatePath params shape
 */
function translateUndefinedToNull<T extends Record<string, string | undefined>>(
  input: T,
): Record<keyof T, string | null> {
  return Object.fromEntries(
    objectEntries(input).map(([key, value]) => [key, value === undefined ? null : value] as const),
  ) as Record<keyof T, string | null>
}

/**
 * A small type-safe wrapper around `Object.entries`
 *
 * By default, typescript makes the entries keys `string` typed only (as it does for Object.keys)
 * this assumes a bit more stable structure around the entries
 */
const objectEntries = <T extends object>(obj: T) => {
  return Object.entries(obj) as Array<[keyof typeof obj, (typeof obj)[keyof typeof obj]]>
}
